import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';


const ARCHIVE_TYPE_OPTIONS = [
  {
    value: 'FULL',
    label: 'Full Configuration',
    description: 'Every component you check below.'
  },
  {
    value: 'AGENTS_ONLY',
    label: 'Agents Only',
    description: 'Agent user accounts only.'
  },
  {
    value: 'ROUTING_ONLY',
    label: 'Routing Configuration Only',
    description: 'Routing Profiles, Queues, Hours of Operation, Quick Connects, and Application Routing Policies. Does not include Agents.'
  },
  {
    value: 'AGENTS_AND_ROUTING',
    label: 'Agents and Routing',
    description: 'Agents, plus everything included in "Routing Configuration Only" above.'
  }
];

const FULL_CONFIG_COMPONENTS = [
  { key: 'agents', label: 'Agents' },
  { key: 'agentRoutingProfiles', label: 'Routing Profiles', description: 'Connect Routing Profiles assigned to agents (queues, channels, concurrency).' },
  { key: 'queues', label: 'Queue' },
  { key: 'attributes', label: 'Attributes' },
  { key: 'securityProfiles', label: 'Security Profiles' },
  { key: 'hierarchyGroups', label: 'Hierarchy Groups' },
  { key: 'hoursOfOperation', label: 'Hours Of Operation' },
  { key: 'quickConnects', label: 'Quick Connects' },
  { key: 'contactFlows', label: 'Contact Flows' },
  { key: 'phoneNumbers', label: 'Phone Numbers' },
  { key: 'prompts', label: 'Prompts' },
  { key: 'flowModules', label: 'Flow Modules' },
  { key: 'applicationRoutingPolicies', label: 'Application Routing Policies' }
];

const CreateArchivePage = () => {
  const [searchParams] = useSearchParams();
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [createArchiveForm, setCreateArchiveForm] = useState({
    instanceId: searchParams.get('instanceId') || '',
    archiveType: 'FULL'
  });
  const [selectedComponents, setSelectedComponents] = useState(
    FULL_CONFIG_COMPONENTS.reduce((acc, component) => {
      acc[component.key] = true;
      return acc;
    }, {})
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  const allComponentsSelected = FULL_CONFIG_COMPONENTS.every(
    (component) => selectedComponents[component.key]
  );

  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();
    
  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;

//   const getUserRole = () => (
//     JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
//   ); 

    // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    setResult(null);
    setError(null);
    setCreateArchiveForm({ instanceId: '', archiveType: 'FULL' });
  };

  const handleInstanceChange = (e) => {
    const instanceId = e.target.value;
    setError(null);
    setResult(null);

    setCreateArchiveForm(prevForm => ({
      ...prevForm,
      instanceId
    }));  
  };

  const handleArchiveNameChange = (e) => {
    setCreateArchiveForm((prev) => ({ ...prev, archiveName: e.target.value }));
  };

  const handleArchiveTypeChange = (e) => {
    setCreateArchiveForm((prev) => ({ ...prev, archiveType: e.target.value }));
  };

  const handleComponentToggle = (key) => {
    setSelectedComponents((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelectAllComponents = (e) => {
    const checked = e.target.checked;
    setSelectedComponents(
      FULL_CONFIG_COMPONENTS.reduce((acc, component) => {
        acc[component.key] = checked;
        return acc;
      }, {})
    );
  };

  const handleCancel = () => {
    setCreateArchiveForm({ instanceId: '', archiveType: 'FULL' });
    setSelectedComponents(
      FULL_CONFIG_COMPONENTS.reduce((acc, component) => {
        acc[component.key] = true;
        return acc;
      }, {})
    );
    setResult(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!createArchiveForm.instanceId) {
      setError('Select an instance before creating an archive');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        instanceId: createArchiveForm.instanceId,
        archiveName: createArchiveForm.archiveName,
        archiveType: createArchiveForm.archiveType,
        ...(createArchiveForm.archiveType === 'FULL' ? { components: selectedComponents } : {})
      };
      const response = await apiService.post(apiEndpoints.createArchiveApi, payload);
      if (response.data.status === 'ok') {
        setResult(response.data.result?.archive || response.data.result);
      } else {
        setError(response.data.message || 'Failed to create archive');
      }
    } catch (err) {
      setError(err.message || 'Error creating archive');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };
    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);


  return (
       <div className="setup-page">
         <div className="page-header">
           <div>
               <h1>Create Connect Archive</h1>
                 <p className="page-subtitle">Create a new Connect archive</p>
           </div>
           <Link to="/archive-management" className="btn btn-secondary">
                ← Back to Manage Archives
           </Link>
         </div>
   
         {/* Environment Selector */}
         <div className="environment-selector" style={{
           backgroundColor: '#f9f9f9',
           border: '2px solid #e0e0e0',
           borderRadius: '8px',
           padding: '16px',
           marginBottom: '20px',
           display: 'flex',
           alignItems: 'center',
           gap: '12px'
         }}>
           <label htmlFor="environment" style={{ 
             fontWeight: 'bold', 
             fontSize: '16px',
             minWidth: '120px'
           }}>
             Environment:
           </label>
           <select
             id="environment"
             value={selectedEnvironment}
             onChange={handleEnvironmentChange}
             style={{
               padding: '10px 16px',
               fontSize: '15px',
               borderRadius: '6px',
               border: '2px solid #ccc',
               backgroundColor: 'white',
               cursor: 'pointer',
               fontWeight: '500',
               minWidth: '200px'
             }}
           >
             {availableEnvironments.map(env => (
               <option key={env} value={env}>
                 {ENVIRONMENT_LABELS[env]} ({env})
               </option>
             ))}
           </select>
           <span style={{
             padding: '6px 12px',
             borderRadius: '4px',
             fontSize: '13px',
             fontWeight: 'bold',
             color: 'white',
             backgroundColor: getEnvironmentColor(selectedEnvironment)
           }}>
             {selectedEnvironment}
           </span>
         </div>

        <div className="form-container">
        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-section">
            <h3>Create Connect Archive</h3>

            <div className="form-group">
                <label htmlFor="instanceId">Instance *</label>
                <select
                  id="instanceId"
                  value={createArchiveForm.instanceId}
                  onChange={handleInstanceChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
            </div>  

            <div className="form-group">
              <label htmlFor="archiveName">Archive Name *</label>
              <input
                id="archiveName"
                type="text"
                value={createArchiveForm.archiveName}
                onChange={handleArchiveNameChange}
                required
              />
              <small>Enter a name for the archive</small>
              
            </div> 

            <fieldset className="allowed-values-fieldset">
              <legend>Archive Type *</legend>
                 <div className="form-group">
                   {ARCHIVE_TYPE_OPTIONS.map((option) => (
                     <div key={option.value} style={{ marginBottom: '10px' }}>
                       <label htmlFor={`archiveType-${option.value}`} style={{ display: 'block', fontWeight: 'normal' }}>
                         <input
                           id={`archiveType-${option.value}`}
                           type="radio"
                           name="archiveType"
                           value={option.value}
                           checked={createArchiveForm.archiveType === option.value}
                           onChange={handleArchiveTypeChange}
                         />
                         {' '}{option.label}
                       </label>
                       {option.description && (
                         <small style={{ display: 'block', marginLeft: '22px', color: '#666' }}>{option.description}</small>
                       )}
                     </div>
                   ))}
                 </div>
            </fieldset>

            {createArchiveForm.archiveType === 'FULL' && (
              <fieldset className="allowed-values-fieldset">
                <legend>Configuration Components</legend>
                <div className="form-group">
                  <label htmlFor="component-all" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    <input
                      id="component-all"
                      type="checkbox"
                      checked={allComponentsSelected}
                      onChange={handleSelectAllComponents}
                    />
                    {' '}All
                  </label>
                  {FULL_CONFIG_COMPONENTS.map((component) => (
                    <div key={component.key} style={{ marginBottom: '8px', marginLeft: '20px' }}>
                      <label htmlFor={`component-${component.key}`} style={{ display: 'block', fontWeight: 'normal' }}>
                        <input
                          id={`component-${component.key}`}
                          type="checkbox"
                          checked={!!selectedComponents[component.key]}
                          onChange={() => handleComponentToggle(component.key)}
                        />
                        {' '}{component.label}
                      </label>
                      {component.description && (
                        <small style={{ display: 'block', marginLeft: '22px', color: '#666' }}>{component.description}</small>
                      )}
                    </div>
                  ))}
                </div>
              </fieldset>
            )}

            {error && (
                <div className="alert alert-error">
                <h4>Error</h4>
                <p>{error}</p>
                </div>
            )}
            {result && (
                <div className="alert alert-success">
                <h4>Success</h4>
                <p>Archive {result.archiveId} created with {result.resources} resource(s).</p>
                {Array.isArray(result.resourceCounts) && result.resourceCounts.length > 0 && (
                  <table className="archive-resource-counts">
                    <thead>
                      <tr>
                        <th>Resource Type</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.resourceCounts.map((entry) => (
                        <tr key={entry.component}>
                          <td>{entry.label}</td>
                          <td>{entry.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || !createArchiveForm.instanceId}>
                {loading ? 'Creating...' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateArchivePage;